<template>
    <div v-if="global" class="ds-exchange-global-filter-menu">
        <div v-for="group in groups" :key="group.key" class="ds-exchange-global-filter-group">
            <div class="ds-exchange-global-filter-group-header">
                <div class="ds-exchange-global-filter-group-title">{{ group.title }}</div>
                <button type="button" class="ds-exchange-global-filter-group-chevron" @click="toggleGroup(group.key)">
                    <icon width="9" height="13" :class="openGroups[group.key] ? '-rotate-90' : 'rotate-90'"><IconChevronRight /></icon>
                </button>
            </div>
            <div v-if="openGroups[group.key]" class="ds-exchange-global-filter-group-body ds-filter-list">
                <input v-if="group.items.length > 5" v-model="searches[group.key]" class="ds-filter-search ds-field-input" placeholder="Поиск" maxlength="100">
                <div v-for="item in visibleItems(group.key, group.items)" :key="item.id || item.value || item.name" class="ds-filter-option" :class="{ 'ds-filter-option-selected': item.check }">
                    <checkbox :model-value="item.check" size="sm" layout="inline" @update:model-value="updateItem(group.key, item, $event)">
                        <span class="ds-filter-option-label">{{ item.name }}</span>
                    </checkbox>
                </div>
            </div>
        </div>

        <div class="ds-exchange-global-filter-group">
            <div class="ds-exchange-global-filter-group-header">
                <div class="ds-exchange-global-filter-group-title">Дата</div>
                <button type="button" class="ds-exchange-global-filter-group-chevron" @click="dateOpen = !dateOpen">
                    <icon width="9" height="13" :class="dateOpen ? '-rotate-90' : 'rotate-90'"><IconChevronRight /></icon>
                </button>
            </div>
            <div v-if="dateOpen" class="ds-exchange-global-filter-group-body">
                <AppCalendar mode="range" :model-value="dateRange" @update:model-value="onDateRangeUpdate" />
                <button v-if="dateRange" type="button" class="ds-filter-action mt-2" @click="clearDateRange">Очистить дату</button>
            </div>
        </div>

        <div class="ds-exchange-global-filter-footer">
            <button type="button" class="ds-filter-action" @click="emit('reset')">Сбросить</button>
        </div>
    </div>

    <div v-else class="ds-filter-popover" :style="width ? { width } : undefined">
        <div class="ds-filter-list">
            <div v-for="item in model" :key="item.id || item.value || item.name" class="ds-filter-option" :class="{ 'ds-filter-option-selected': item.check }">
                <checkbox v-model="item.check" size="sm" layout="inline" @update:model-value="emit('check')">
                    <span class="ds-filter-option-label">{{ item.name }}</span>
                </checkbox>
            </div>
        </div>
        <div class="ds-filter-footer">
            <button type="button" class="ds-filter-action" @click="resetColumn">Сбросить</button>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, reactive, ref } from 'vue'
import type { Dayjs } from 'dayjs'
import AppCalendar from '@/ui/AppCalendar.vue'
import IconChevronRight from '@/ui/icons/IconChevronRight.vue'

interface FilterItem {
    id?: string
    value?: string
    name: string
    check: boolean
}

type FilterGroupKey = 'positions' | 'departments' | 'statuses' | 'types'

const props = withDefaults(defineProps<{
    global?: boolean
    width?: string
    positionsItems?: FilterItem[]
    departmentsItems?: FilterItem[]
    statusesItems?: FilterItem[]
    typesItems?: FilterItem[]
    dateRange?: [Dayjs, Dayjs] | null
}>(), {
    global: false,
    departmentsItems: () => [],
    statusesItems: () => [],
    dateRange: null
})

const model = defineModel<FilterItem[]>({ default: () => [] })
const emit = defineEmits(['update:positionsItems', 'update:departmentsItems', 'update:statusesItems', 'update:typesItems', 'update:dateRange', 'check', 'reset'])

const openGroups = reactive<Record<FilterGroupKey, boolean>>({ positions: true, departments: true, statuses: true, types: true })
const searches = reactive<Record<FilterGroupKey, string>>({ positions: '', departments: '', statuses: '', types: '' })
const dateOpen = ref(true)

const groups = computed(() => {
    const result: Array<{ key: FilterGroupKey; title: string; items: FilterItem[] }> = [
        { key: 'departments', title: 'Подразделение', items: props.departmentsItems },
        { key: 'statuses', title: 'Статус', items: props.statusesItems }
    ]
    if (props.positionsItems) result.unshift({ key: 'positions', title: 'Должность', items: props.positionsItems })
    if (props.typesItems) result.splice(result.length - 1, 0, { key: 'types', title: 'Тип запроса', items: props.typesItems })
    return result
})

const toggleGroup = (key: FilterGroupKey) => { openGroups[key] = !openGroups[key] }
const visibleItems = (key: FilterGroupKey, items: FilterItem[]) => items.filter(item => item.name.toLowerCase().includes(searches[key].toLowerCase()))

function updateItem(key: FilterGroupKey, item: FilterItem, checked: boolean) {
    const group = groups.value.find(current => current.key === key)
    if (!group) return
    const value = group.items.map(current => current === item ? { ...current, check: checked } : current)
    emit(`update:${key}Items`, value)
    emit('check')
}

function onDateRangeUpdate(value: Dayjs | [Dayjs, Dayjs] | Dayjs[]) {
    if (!Array.isArray(value) || value.length < 2) return
    const [start, end] = value as [Dayjs, Dayjs]
    if (!start || !end) return
    emit('update:dateRange', [start, end])
    emit('check')
}

function clearDateRange() {
    emit('update:dateRange', null)
    emit('check')
}

function resetColumn() {
    emit('reset')
    emit('check')
}
</script>
